export interface BootstrapRequest {
	storeUrl: string
	profileName?: string
	defaultCurrency?: string
	runExtractor?: boolean
	extractorCommand?: string
	sourceProductsPath?: string
}

export interface StoreNotBootstrappedError extends Error {
	code?: string
	bootstrapRequired?: boolean
	bootstrapDefaults?: {
		profileName?: string
		runExtractor?: boolean
		defaultCurrency?: string
	}
}

export interface ReverseDemoStore {
	storeSlug: string
	storeUrl: string
	organizationId: string
	profileName: string
	selectedCollections: string[]
	createdAt?: number
	updatedAt?: number
	lastBootstrappedAt?: number
}

interface ReverseDemoSessionResponse {
	success: boolean
	externalSessionId: string
	bootstrapped?: boolean
	bootstrapResult?: {
		organizationId: string
		storeSlug: string
		selectedCollections: string[]
		uploadedCounts: Record<string, number>
	}
	storeSlug: string
	organizationId: string
	selectedCollections: string[]
	tokens: {
		wsToken: string
		wsExpiresIn: number
		backendToken: string
		backendExpiresIn: number
	}
}

function parseApiError(payload: any, fallback: string): StoreNotBootstrappedError {
	const error = new Error(payload?.error || fallback) as StoreNotBootstrappedError
	error.code = payload?.code
	error.bootstrapRequired = Boolean(payload?.bootstrapRequired)
	error.bootstrapDefaults = payload?.bootstrapDefaults
	return error
}

export class ReverseDemoService {
	private static instance: ReverseDemoService

	static getInstance(): ReverseDemoService {
		if (!ReverseDemoService.instance) {
			ReverseDemoService.instance = new ReverseDemoService()
		}
		return ReverseDemoService.instance
	}

	private getBackendUrl(): string {
		return import.meta.env.VITE_BACKEND_URL || window.location.origin
	}

	async createSession(storeUrl: string): Promise<ReverseDemoSessionResponse> {
		const response = await fetch(`${this.getBackendUrl()}/api/v1/search/reverse-demo/session`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ storeUrl }),
		})

		if (!response.ok) {
			const error = await response.json().catch(() => ({}))
			throw parseApiError(error, "Failed to create reverse-demo session")
		}

		return response.json()
	}

	async bootstrapStore(payload: BootstrapRequest, operator: Record<string, string>) {
		const response = await fetch(`${this.getBackendUrl()}/api/v1/search/bootstrap/store-auto`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...operator,
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const error = await response.json().catch(() => ({}))
			throw parseApiError(error, "Failed to bootstrap store")
		}

		return response.json()
	}

	async listStores(operator: Record<string, string>): Promise<ReverseDemoStore[]> {
		const response = await fetch(`${this.getBackendUrl()}/api/v1/search/reverse-demo/stores`, {
			headers: operator,
		})

		if (!response.ok) {
			const error = await response.json().catch(() => ({}))
			throw new Error(error.error || "Failed to list stores")
		}

		const data = await response.json()
		return data.stores || []
	}

	async deleteStore(storeSlug: string, mode: "soft" | "hard", operator: Record<string, string>, confirm: boolean = true) {
		const response = await fetch(
			`${this.getBackendUrl()}/api/v1/search/reverse-demo/stores/${encodeURIComponent(storeSlug)}?mode=${mode}&confirm=${confirm ? "true" : "false"}`,
			{
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
					...operator,
				},
			},
		)

		if (!response.ok) {
			const error = await response.json().catch(() => ({}))
			throw new Error(error.error || "Failed to delete store")
		}

		return response.json()
	}

	/** Operator only: Clerk emails an invitation to administer the store's organization. */
	async inviteAdmin(storeSlug: string, email: string, operator: Record<string, string>) {
		const response = await fetch(`${this.getBackendUrl()}/api/v1/search/operator/invite`, {
			method: "POST",
			headers: { "Content-Type": "application/json", ...operator },
			body: JSON.stringify({ storeSlug, email }),
		})
		const result = await response.json().catch(() => ({}))
		if (!response.ok) throw new Error(result.error || "Could not send the invitation")
		return result
	}
}
